/**
 * Recurring charges — manager-configured monthly non-rent charges (water,
 * garbage, electricity, etc.) that `generateMonthlyInvoices` (Node backend)
 * bills automatically alongside rent. Node-only feature, no Django
 * counterpart — see refactor/backend/src/modules/recurringCharges and
 * refactor/docs/PARITY_NOTES.md.
 *
 * Only mounted under the manager branches of billingRoutes (router.tsx) — a
 * tenant's route tree never registers this path at all, same reasoning
 * InvoiceListPage documents for its own manager-only actions.
 *
 * Three scope modes (a landlord picks one per charge):
 *   - LEASE: one tenant, a fixed amount.
 *   - PROPERTY: every active tenant under the property, the SAME fixed amount each.
 *   - PROPERTY_SHARED: one fixed TOTAL split evenly across however many active
 *     tenants exist under the property that month.
 * Two bases: FLAT (fixed amount, billed automatically every month) or
 * METERED (water/electricity-style — amount = consumption * rate, billed via
 * the explicit "Record reading" action below since there's no automatic
 * meter data source). METERED only combines with scope_mode LEASE.
 */
import { Fragment, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  createRecurringCharge,
  listRecurringCharges,
  recordReading,
  updateRecurringCharge,
} from '../../api/endpoints/recurringCharges';
import { listLeases } from '../../api/endpoints/tenants';
import { listProperties } from '../../api/endpoints/properties';
import {
  METERED_CHARGE_TYPES,
  RECURRING_CHARGE_BASES,
  RECURRING_CHARGE_SCOPE_MODES,
  RECURRING_CHARGE_TYPES,
  type RecurringCharge,
  type RecurringChargeBasis,
  type RecurringChargeCreateRequest,
  type RecurringChargeScopeMode,
  type RecurringChargeType,
} from '../../types/recurringCharge';

const INITIAL_FORM = {
  scope_mode: 'LEASE' as RecurringChargeScopeMode,
  charge_basis: 'FLAT' as RecurringChargeBasis,
  charge_type: 'GARBAGE' as RecurringChargeType,
  lease: '',
  property: '',
  amount: '',
  rate: '',
  notes: '',
};

type FormState = typeof INITIAL_FORM;

const SCOPE_LABEL: Record<RecurringChargeScopeMode, string> = {
  LEASE: 'One lease',
  PROPERTY: 'Every tenant in a property (same amount each)',
  PROPERTY_SHARED: 'Every tenant in a property (total split evenly)',
};

export function RecurringChargeListPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [page, setPage] = useState(1);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState<number | null>(null);
  const [recordingChargeId, setRecordingChargeId] = useState<number | null>(null);
  const [readingValue, setReadingValue] = useState('');

  const query = useQuery({
    queryKey: ['recurring-charges', { page }],
    queryFn: () => listRecurringCharges({ page }),
  });

  const invalidateCharges = () => void queryClient.invalidateQueries({ queryKey: ['recurring-charges'] });

  const toggleActive = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      updateRecurringCharge(id, { is_active }),
    onSuccess: invalidateCharges,
  });

  const recording = useMutation({
    mutationFn: ({ id, reading }: { id: number; reading: string }) => recordReading(id, { reading }),
    onSuccess: () => {
      invalidateCharges();
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // Deliberately does NOT close the inline form here — the manager needs
      // to actually see the "previous -> new (consumption billed)" result
      // this just unlocked; closing immediately would race the confirmation
      // off-screen before it could ever render. They dismiss it themselves
      // via Cancel once they've seen it. Only the input clears, so a second
      // reading on the same charge doesn't start pre-filled with the last one.
      setReadingValue('');
    },
  });

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  function update<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  function handleScopeModeChange(scopeMode: RecurringChargeScopeMode): void {
    setForm((previous) => ({
      ...previous,
      scope_mode: scopeMode,
      // METERED only combines with LEASE — force FLAT (and drop any rate) the
      // moment the scope moves away from LEASE, so the form can never hold an
      // invalid combination the backend would reject.
      charge_basis: scopeMode === 'LEASE' ? previous.charge_basis : 'FLAT',
      rate: scopeMode === 'LEASE' ? previous.rate : '',
      charge_type:
        scopeMode !== 'LEASE' && !RECURRING_CHARGE_TYPES.includes(previous.charge_type)
          ? 'GARBAGE'
          : previous.charge_type,
    }));
  }

  function handleChargeBasisChange(basis: RecurringChargeBasis): void {
    setForm((previous) => ({
      ...previous,
      charge_basis: basis,
      rate: basis === 'METERED' ? previous.rate : '',
      charge_type:
        basis === 'METERED' && !METERED_CHARGE_TYPES.includes(previous.charge_type as 'WATER' | 'ELECTRICITY')
          ? 'WATER'
          : previous.charge_type,
    }));
  }

  const leasesForAssignment = useQuery({
    queryKey: ['leases', 'for-assignment'],
    queryFn: () => listLeases({ page: 1 }),
    enabled: form.scope_mode === 'LEASE',
  });

  const propertiesForAssignment = useQuery({
    queryKey: ['properties', 'for-assignment'],
    queryFn: () => listProperties({ page: 1 }),
    enabled: form.scope_mode !== 'LEASE',
  });

  const create = useMutation({
    mutationFn: () => {
      const payload: RecurringChargeCreateRequest = {
        scope_mode: form.scope_mode,
        charge_basis: form.charge_basis,
        charge_type: form.charge_type,
        amount: form.amount,
        notes: form.notes === '' ? undefined : form.notes,
      };
      if (form.scope_mode === 'LEASE') {
        payload.lease = Number(form.lease);
      } else {
        payload.property = Number(form.property);
      }
      if (form.charge_basis === 'METERED') {
        payload.rate = form.rate;
      }
      return createRecurringCharge(payload);
    },
    onSuccess: () => {
      setForm(INITIAL_FORM);
      setError(null);
      setFieldErrors({});
      invalidateCharges();
    },
    onError: (caught) => {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    },
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    create.mutate();
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  const charges = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  function target(charge: RecurringCharge): string {
    return charge.scope_mode === 'LEASE'
      ? (charge.lease_display ?? '—')
      : `${charge.property_name ?? '—'} (${SCOPE_LABEL[charge.scope_mode]})`;
  }

  function renderRowActions(charge: RecurringCharge): React.JSX.Element {
    return (
      <span className="admin-controls">
        {charge.is_active ? (
          <button
            type="button"
            disabled={toggleActive.isPending && toggleActive.variables?.id === charge.id}
            onClick={() => setConfirmDeactivateId(charge.id)}
          >
            Deactivate
          </button>
        ) : (
          <button
            type="button"
            disabled={toggleActive.isPending && toggleActive.variables?.id === charge.id}
            onClick={() => toggleActive.mutate({ id: charge.id, is_active: true })}
          >
            Reactivate
          </button>
        )}
        {charge.charge_basis === 'METERED' && charge.is_active && (
          <>
            {' '}
            <button
              type="button"
              onClick={() => {
                setRecordingChargeId(recordingChargeId === charge.id ? null : charge.id);
                setReadingValue('');
              }}
            >
              Record reading
            </button>
          </>
        )}
      </span>
    );
  }

  return (
    <section>
      <h2>Recurring charges</h2>
      <p>
        Monthly non-rent charges (water, garbage, electricity, and similar) that get billed
        automatically alongside rent.
      </p>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && charges.length === 0 && (
        <EmptyState icon={Receipt} message="No recurring charges configured yet." />
      )}

      {query.isSuccess &&
        charges.length > 0 &&
        (isMobile ? (
          <ul className="record-cards">
            {charges.map((charge) => (
              <li key={charge.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Type</span>
                  <span className="record-card__value">
                    {charge.charge_type} ({charge.charge_basis === 'METERED' ? 'metered' : 'flat'})
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Target</span>
                  <span className="record-card__value">{target(charge)}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Amount</span>
                  <span className="record-card__value">
                    {charge.charge_basis === 'METERED' ? `${charge.rate}/unit` : charge.amount}
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Status</span>
                  <span className="record-card__value">{charge.is_active ? 'Active' : 'Inactive'}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Actions</span>
                  <span className="record-card__value">{renderRowActions(charge)}</span>
                </div>
                {recordingChargeId === charge.id && (
                  <RecordReadingForm
                    value={readingValue}
                    onChange={setReadingValue}
                    onSubmit={() => recording.mutate({ id: charge.id, reading: readingValue })}
                    onCancel={() => setRecordingChargeId(null)}
                    isPending={recording.isPending}
                    result={recording.isSuccess && recording.variables?.id === charge.id ? recording.data : undefined}
                  />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Type</th>
                <th>Scope</th>
                <th>Target</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((charge) => (
                <Fragment key={charge.id}>
                  <tr>
                    <td>
                      {charge.charge_type} ({charge.charge_basis === 'METERED' ? 'metered' : 'flat'})
                    </td>
                    <td>{SCOPE_LABEL[charge.scope_mode]}</td>
                    <td>{target(charge)}</td>
                    <td>{charge.charge_basis === 'METERED' ? `${charge.rate}/unit` : charge.amount}</td>
                    <td>{charge.is_active ? 'Active' : 'Inactive'}</td>
                    <td>{renderRowActions(charge)}</td>
                  </tr>
                  {recordingChargeId === charge.id && (
                    <tr>
                      <td colSpan={6}>
                        <RecordReadingForm
                          value={readingValue}
                          onChange={setReadingValue}
                          onSubmit={() => recording.mutate({ id: charge.id, reading: readingValue })}
                          onCancel={() => setRecordingChargeId(null)}
                          isPending={recording.isPending}
                          result={
                            recording.isSuccess && recording.variables?.id === charge.id
                              ? recording.data
                              : undefined
                          }
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        ))}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>

      <h3>Add recurring charge</h3>
      <form onSubmit={handleSubmit} noValidate>
        {error !== null && (
          <p role="alert" className="form__error">
            {error}
          </p>
        )}

        <label htmlFor="charge-scope-mode">Applies to</label>
        <select
          id="charge-scope-mode"
          value={form.scope_mode}
          onChange={(event) => handleScopeModeChange(event.target.value as RecurringChargeScopeMode)}
        >
          {RECURRING_CHARGE_SCOPE_MODES.map((value) => (
            <option key={value} value={value}>
              {SCOPE_LABEL[value]}
            </option>
          ))}
        </select>

        <label htmlFor="charge-basis">Billing basis</label>
        <select
          id="charge-basis"
          value={form.charge_basis}
          disabled={form.scope_mode !== 'LEASE'}
          onChange={(event) => handleChargeBasisChange(event.target.value as RecurringChargeBasis)}
        >
          {RECURRING_CHARGE_BASES.map((value) => (
            <option key={value} value={value}>
              {value === 'METERED' ? 'Metered (rate x usage)' : 'Flat amount'}
            </option>
          ))}
        </select>
        {form.scope_mode !== 'LEASE' && (
          <p className="form__hint">Metered charges can only apply to a single lease.</p>
        )}

        <label htmlFor="charge-type">Charge type</label>
        <select
          id="charge-type"
          value={form.charge_type}
          onChange={(event) => update('charge_type', event.target.value as RecurringChargeType)}
        >
          {(form.charge_basis === 'METERED' ? METERED_CHARGE_TYPES : RECURRING_CHARGE_TYPES).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>

        {form.scope_mode === 'LEASE' ? (
          <>
            <label htmlFor="charge-lease">Lease</label>
            <select id="charge-lease" required value={form.lease} onChange={(event) => update('lease', event.target.value)}>
              <option value="">— Select a lease —</option>
              {(leasesForAssignment.data?.results ?? []).map((lease) => (
                <option key={lease.id} value={lease.id}>
                  {lease.unit_number} — {lease.tenant_name} ({lease.property_name})
                </option>
              ))}
            </select>
            {fieldError('lease') !== undefined && <p className="form__field-error">{fieldError('lease')}</p>}
          </>
        ) : (
          <>
            <label htmlFor="charge-property">Property</label>
            <select
              id="charge-property"
              required
              value={form.property}
              onChange={(event) => update('property', event.target.value)}
            >
              <option value="">— Select a property —</option>
              {(propertiesForAssignment.data?.results ?? []).map((property) => (
                <option key={property.id} value={property.id}>
                  {property.name}
                </option>
              ))}
            </select>
            {fieldError('property') !== undefined && <p className="form__field-error">{fieldError('property')}</p>}
          </>
        )}

        <label htmlFor="charge-amount">
          {form.scope_mode === 'PROPERTY_SHARED' ? 'Total monthly amount (split evenly)' : 'Amount per tenant'}
        </label>
        <input
          id="charge-amount"
          inputMode="decimal"
          required
          value={form.amount}
          onChange={(event) => update('amount', event.target.value)}
        />
        {fieldError('amount') !== undefined && <p className="form__field-error">{fieldError('amount')}</p>}

        {form.charge_basis === 'METERED' && (
          <>
            <label htmlFor="charge-rate">Rate per unit consumed</label>
            <input
              id="charge-rate"
              inputMode="decimal"
              required
              value={form.rate}
              onChange={(event) => update('rate', event.target.value)}
            />
            {fieldError('rate') !== undefined && <p className="form__field-error">{fieldError('rate')}</p>}
          </>
        )}

        <label htmlFor="charge-notes">Notes (optional)</label>
        <textarea id="charge-notes" value={form.notes} onChange={(event) => update('notes', event.target.value)} />

        {fieldError('charge_type') !== undefined && <p className="form__field-error">{fieldError('charge_type')}</p>}

        <button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Adding…' : 'Add recurring charge'}
        </button>
        {create.isSuccess && <span role="status"> Recurring charge added.</span>}
      </form>

      <ConfirmDialog
        open={confirmDeactivateId !== null}
        title="Deactivate recurring charge"
        message="Deactivate this recurring charge? It will stop being billed automatically. This can be undone by reactivating it."
        tone="danger"
        confirmLabel="Deactivate"
        onConfirm={() => {
          if (confirmDeactivateId !== null) {
            toggleActive.mutate({ id: confirmDeactivateId, is_active: false });
          }
          setConfirmDeactivateId(null);
        }}
        onCancel={() => setConfirmDeactivateId(null)}
      />
    </section>
  );
}

interface RecordReadingFormProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isPending: boolean;
  result: { previous_reading: string; new_reading: string; consumption?: string } | undefined;
}

function RecordReadingForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  isPending,
  result,
}: RecordReadingFormProps): React.JSX.Element {
  return (
    <div className="inline-form">
      <label htmlFor="new-meter-reading">New meter reading</label>
      <input
        id="new-meter-reading"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        disabled={isPending || value === ''}
        onClick={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        {isPending ? 'Saving…' : 'Save reading'}
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
      {result !== undefined && (
        <p role="status">
          Recorded: {result.previous_reading} → {result.new_reading}
          {result.consumption !== undefined && ` (${result.consumption} units billed)`}
        </p>
      )}
    </div>
  );
}

export default RecurringChargeListPage;
